import { OMS_COLORS } from '../omsDashTheme';
import type { HealthScore } from '../utils/healthScore';
import { BandChip, DeltaChip, ScoreDial, ScoreExplainer } from './HealthScore';

/**
 * THE health-score display — the only way a score may appear on the
 * dashboard ("no naked numbers" rule). Ring gauge with value + /100 inside,
 * band chip beside it, "Health score" microlabel, and the info icon opening
 * the score breakdown. Scores never render as bare numerals, so they can
 * never be confused with a task count.
 */
interface Props {
  score: HealthScore | null;
  /** Entity name for the accessible dial label. */
  name: string;
  /** Dial diameter in px. Keep ≥40 so the /100 stays legible. */
  size?: number;
  /** Pass to show "▲ N pts <deltaPhrase>" under the chip; omit to hide. */
  delta?: number | null;
  /** Comparison window, e.g. "vs last week". */
  deltaPhrase?: string;
  microlabel?: string;
  /** Hide the band chip in ultra-tight rows (dial + microlabel remain). */
  showChip?: boolean;
  /** Show the ⓘ explainer opening the component-by-component breakdown. */
  showExplainer?: boolean;
  /** 'end' right-aligns the text column (for right-edge row slots). */
  align?: 'start' | 'end';
}

export default function ScoreGauge({
  score,
  name,
  size = 48,
  delta,
  deltaPhrase = 'vs previous window',
  microlabel = 'Health score',
  showChip = true,
  showExplainer = true,
  align = 'start',
}: Props) {
  const textAlign = align === 'end' ? 'items-end text-right' : 'items-start text-left';
  return (
    <span className="inline-flex items-center gap-2">
      {align === 'end' && (
        <TextColumn
          score={score}
          microlabel={microlabel}
          showChip={showChip}
          showExplainer={showExplainer}
          delta={delta}
          deltaPhrase={deltaPhrase}
          className={textAlign}
        />
      )}
      <ScoreDial score={score} size={size} label={`${microlabel} for ${name}`} />
      {align === 'start' && (
        <TextColumn
          score={score}
          microlabel={microlabel}
          showChip={showChip}
          showExplainer={showExplainer}
          delta={delta}
          deltaPhrase={deltaPhrase}
          className={textAlign}
        />
      )}
    </span>
  );
}

function TextColumn({
  score,
  microlabel,
  showChip,
  showExplainer,
  delta,
  deltaPhrase,
  className,
}: {
  score: HealthScore | null;
  microlabel: string;
  showChip: boolean;
  showExplainer: boolean;
  delta: number | null | undefined;
  deltaPhrase: string;
  className: string;
}) {
  return (
    <span className={`flex min-w-0 flex-col gap-0.5 ${className}`}>
      <span
        className="flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-[0.12em]"
        style={{ color: OMS_COLORS.caption }}
      >
        {microlabel}
        {showExplainer && score && <ScoreExplainer score={score} />}
      </span>
      {showChip && <BandChip score={score} />}
      {delta !== undefined && <DeltaChip delta={delta} phrase={`pts ${deltaPhrase}`} />}
    </span>
  );
}
